import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Brain, CheckCircle, Target, AlertCircle, Home, ArrowRight } from 'lucide-react';
import SkillsAssessmentChat from '../components/assessment/SkillsAssessmentChat';
import LearningPlanDisplay from '../components/assessment/LearningPlanDisplay';
import { AssessmentResultsReveal } from '../components/assessment/AssessmentResultsReveal';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/common/Layout';
import { ROUTES } from '../utils/constants';
import { awardXP, isTaskCompleted } from '../services/firebase/gamification';
import { SEOHead } from '../components/seo/SEOHead';
import { AssessmentResult } from '../services/skillsAssessment';

type Step = 'intro' | 'assessment' | 'results' | 'plan';

const SkillsAssessmentPage: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState<Step>('intro');
  const [assessmentId, setAssessmentId] = useState<string | null>(null);
  const [extractedData, setExtractedData] = useState<AssessmentResult['extractedData'] | undefined>();

  // Unauthenticated visitors should use the hero card on the homepage,
  // not the full assessment page.
  useEffect(() => {
    if (!isAuthenticated) {
      navigate(ROUTES.home, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // If the user already completed an assessment (either via the hero card before
  // registering, or a previous session), jump straight to the plan view.
  useEffect(() => {
    // 1. Check sessionStorage (same-tab: hero card → register → verify → here)
    const raw = sessionStorage.getItem('heroAssessmentAnswers');
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as { assessmentId?: string };
        if (parsed.assessmentId) {
          setAssessmentId(parsed.assessmentId);
          setCurrentStep('plan');
          sessionStorage.removeItem('heroAssessmentAnswers');
          return;
        }
      } catch { /* malformed — ignore */ }
      sessionStorage.removeItem('heroAssessmentAnswers');
    }

    // 2. Check user profile (cross-tab: e.g. email verification opened in a new tab)
    if (user?.skillsAssessmentCompleted) {
      setCurrentStep('plan');
    }
  }, [user?.skillsAssessmentCompleted]);

  // 12-month package users have full access and don't need the assessment funnel
  if (user?.subscriptionTier === 'twelve_month_package') {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-8 text-center">
            <AlertCircle className="w-16 h-16 text-primary-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-neutral-900 mb-3">
              Skills Assessment Not Available
            </h1>
            <p className="text-neutral-600 mb-6">
              As a 12-month package member you have immediate full access to all platform
              features. The skills assessment is only for users who want a personalised
              learning path recommendation.
            </p>
            <Link
              to={ROUTES.home}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Home className="w-5 h-5" />
              Go to Dashboard
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const handleAssessmentComplete = async (id: string, data?: AssessmentResult['extractedData']) => {
    setAssessmentId(id);
    setExtractedData(data);
    setCurrentStep('results');
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (user?.uid) {
      try {
        const alreadyCompleted = await isTaskCompleted(user.uid, 'take_skills_assessment');
        if (!alreadyCompleted) {
          await awardXP(user.uid, 'take_skills_assessment', 100);
          window.dispatchEvent(new CustomEvent('gamificationUpdate'));
        }
      } catch (xpError) {
        console.error('[Gamification] Error awarding assessment XP:', xpError);
      }
    }
  };

  const steps: { id: Step; title: string; Icon: React.ElementType }[] = [
    { id: 'intro',      title: 'Introduction',     Icon: Brain      },
    { id: 'assessment', title: 'Skills Assessment', Icon: CheckCircle },
    { id: 'results',    title: 'Your Results',      Icon: Target     },
    { id: 'plan',       title: 'Learning Plan',     Icon: Target     },
  ];

  // Only show the first 3 steps in the header (intro/assessment/results),
  // then swap to results/plan once the user reaches results
  const visibleSteps = currentStep === 'plan'
    ? steps.filter(s => ['assessment', 'results', 'plan'].includes(s.id))
    : steps.filter(s => ['intro', 'assessment', 'results'].includes(s.id));

  return (
    <Layout>
      <SEOHead
        title="AI Skills Assessment - trAInedup.ai"
        description="Take our free AI skills assessment to discover your current knowledge level and get a personalised learning plan tailored to your goals."
        canonical="https://trainedup.ai/skills-assessment"
        keywords={['AI skills assessment', 'AI learning plan', 'personalised AI training']}
      />
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-4">
                <Link
                  to={ROUTES.home}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex items-center justify-center"
                  title="Return to Home"
                >
                  <Home className="w-6 h-6 text-gray-600 hover:text-blue-600" />
                </Link>
                <div className="flex flex-col gap-2">
                  <h1 className="text-3xl font-bold text-gray-900">AI Skills Assessment</h1>
                  <p className="text-gray-600">Discover your AI skills and get a personalised learning plan</p>
                </div>
              </div>

              {/* Progress steps */}
              <div className="flex flex-wrap items-center gap-3 sm:gap-4 pl-12 sm:pl-0">
                {visibleSteps.map((step, index) => {
                  const { Icon } = step;
                  const isActive    = step.id === currentStep;
                  const isCompleted = visibleSteps.findIndex(s => s.id === currentStep) > index;
                  return (
                    <div
                      key={step.id}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                        isActive    ? 'border-blue-200 bg-blue-50 text-blue-700'   :
                        isCompleted ? 'border-green-200 bg-green-50 text-green-700' :
                                      'border-gray-200 bg-gray-50 text-gray-600'
                      }`}
                    >
                      <div
                        className={`flex items-center justify-center w-9 h-9 rounded-full ${
                          isActive    ? 'bg-blue-500 text-white'   :
                          isCompleted ? 'bg-green-500 text-white' :
                                        'bg-gray-200 text-gray-600'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                      </div>
                      <span className="text-sm font-medium">{step.title}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

          {/* ── INTRO ── */}
          {currentStep === 'intro' && (
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-blue-500 to-teal-500 px-8 py-12 text-white text-center">
                  <Brain className="w-16 h-16 mx-auto mb-4" />
                  <h2 className="text-3xl font-bold mb-4">It's Not Too Late to Catch Up</h2>
                  <p className="text-xl opacity-90">
                    Wherever you're starting from, let's assess your current AI skills and build a personalised plan to get you up to speed
                  </p>
                </div>

                <div className="px-8 py-8">
                  <div className="grid md:grid-cols-3 gap-6 mb-8">
                    {[
                      { Icon: Brain,       color: 'blue',   title: 'Understand Your Skills',   body: "We'll chat about your current knowledge, experience, and goals in AI." },
                      { Icon: CheckCircle, color: 'teal',   title: 'Identify Knowledge Gaps',   body: 'Our AI will analyse your responses to pinpoint areas for improvement.' },
                      { Icon: Target,      color: 'purple', title: 'Get Your Learning Plan',    body: 'Receive a plan with courses tailored to your goals and skill level.' },
                    ].map(({ Icon, color, title, body }) => (
                      <div key={title} className="text-center">
                        <div className={`w-12 h-12 bg-${color}-100 rounded-lg flex items-center justify-center mx-auto mb-3`}>
                          <Icon className={`w-6 h-6 text-${color}-600`} />
                        </div>
                        <h3 className="font-semibold text-gray-900 mb-2">{title}</h3>
                        <p className="text-gray-600 text-sm">{body}</p>
                      </div>
                    ))}
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6 mb-8">
                    <h3 className="font-semibold text-gray-900 mb-3">What to Expect:</h3>
                    <ul className="space-y-2 text-gray-600">
                      {[
                        '10-minute conversational assessment',
                        'Questions about your background, skills, and goals',
                        'Personalised course recommendations',
                        'Structured learning timeline',
                      ].map((item) => (
                        <li key={item} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="text-center">
                    <button
                      onClick={() => setCurrentStep('assessment')}
                      className="px-8 py-4 bg-gradient-to-r from-blue-500 to-teal-500 text-white rounded-lg hover:from-blue-600 hover:to-teal-600 transition-all font-semibold text-lg flex items-center space-x-2 mx-auto"
                    >
                      <span>Start Your Assessment</span>
                      <ArrowRight className="w-5 h-5" />
                    </button>
                    <p className="text-gray-500 text-sm mt-3">
                      Free · Takes about 5–10 minutes
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── ASSESSMENT ── */}
          {currentStep === 'assessment' && (
            <div className="max-w-4xl mx-auto">
              <SkillsAssessmentChat
                onAssessmentComplete={handleAssessmentComplete}
                className="h-[600px]"
              />
            </div>
          )}

          {/* ── RESULTS ── */}
          {currentStep === 'results' && assessmentId && (
            <div className="max-w-2xl mx-auto">
              <AssessmentResultsReveal
                assessmentId={assessmentId}
                extractedData={extractedData}
                variant="page"
                onViewPlan={() => {
                  setCurrentStep('plan');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            </div>
          )}

          {/* ── PLAN ── */}
          {currentStep === 'plan' && (
            <div className="max-w-6xl mx-auto">
              <LearningPlanDisplay assessmentId={assessmentId || undefined} />
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default SkillsAssessmentPage;
